import { http } from 'interfaces'

export type JobType = 'pidf' | 'pidf_2' | 'lobbylinx' | 'hep' | 'nyne' | 'kw_sync'

export type KWSyncGateNamespace = 'G' | 'E'

export interface KWSyncGateNamespaceCounts {
  total: number
  matched: number
  orphans: number
}

export interface KWSyncGateMetrics {
  kw_sync_gate: Record<KWSyncGateNamespace, KWSyncGateNamespaceCounts>
}

export interface ETLJobListItem {
  id: number
  job_type: JobType
  status: 'pending' | 'running' | 'completed' | 'failed' | 'canceled'
  timestamp: string | null
  current_step: number
  total_steps: number
  latest_message: string | null
  created_at: string
  started_at: string | null
  finished_at: string | null
  is_stalled: boolean
}

export interface MatchRateOverall {
  total: number
  matched: number
  match_rate: number
}

export interface MatchRateStateRow extends MatchRateOverall {
  state: string
}

export interface MatchRateGroupRow extends MatchRateOverall {
  group: string
}

export interface MatchRatePayload {
  overall: MatchRateOverall
  by_state: MatchRateStateRow[]
  by_group: MatchRateGroupRow[]
}

export interface ETLJob extends ETLJobListItem {
  progress_percentage: number
  message: string | null
  error_message: string | null
  updated_at: string
  heartbeat_at: string | null
  cancel_requested: boolean
  metrics?: MatchRatePayload | KWSyncGateMetrics | null
}

export function isKWSyncGateMetrics (metrics: MatchRatePayload | KWSyncGateMetrics): metrics is KWSyncGateMetrics {
  return 'kw_sync_gate' in metrics
}

export interface ETLStep {
  number: number
  name: string
  description: string
}

export interface AristotleJob {
  id: number
  file_name: string
  file_uploaded: boolean
  file_guid: string | null
  voter_match_status: string
  table_name_voters: string | null
  imported: boolean
  source: string | null
  created_at: string
  updated_at: string
}

export async function startETLJob (jobType: JobType = 'pidf', source?: typeof KW_SYNC_SOURCE): Promise<ETLJob> {
  return await http.post('/api/v1/pidf/jobs/start', { job_type: jobType, source })
    .then(response => response.data)
}

export async function cancelETLJob (jobId: number): Promise<ETLJob> {
  return await http.post(`/api/v1/pidf/jobs/${jobId}/cancel`)
    .then(response => response.data)
}

export async function getETLJobStatus (jobId: number): Promise<ETLJob> {
  return await http.get(`/api/v1/pidf/jobs/${jobId}`)
    .then(response => response.data)
}

export async function listETLJobs (limit: number = 10, jobType?: JobType): Promise<ETLJobListItem[]> {
  return await http.get('/api/v1/pidf/jobs', {
    params: {
      limit,
      ...(jobType !== undefined && { job_type: jobType })
    }
  }).then(response => response.data)
}

export const KW_SYNC_SOURCE = 'kw_sync'
export type Phase2Selection = number | typeof KW_SYNC_SOURCE

export async function getETLSteps (jobType: JobType = 'pidf', source?: typeof KW_SYNC_SOURCE, aristotleJobId?: number): Promise<ETLStep[]> {
  return await http.get('/api/v1/pidf/steps', {
    params: { job_type: jobType, source, aristotle_job_id: aristotleJobId }
  }).then(response => response.data)
}

export async function listAristotleJobs (limit: number = 50): Promise<AristotleJob[]> {
  return await http.get('/api/v1/pidf/aristotle-jobs', {
    params: { limit }
  }).then(response => response.data)
}

export async function getAristotleJob (jobId: number): Promise<AristotleJob> {
  return await http.get(`/api/v1/pidf/aristotle-jobs/${jobId}`)
    .then(response => response.data)
}

export async function startPhase2ETLJob (selection: Phase2Selection): Promise<ETLJob> {
  const payload = selection === KW_SYNC_SOURCE ? { source: KW_SYNC_SOURCE } : { aristotle_job_id: selection }
  return await http.post('/api/v1/pidf/jobs/start-phase2', payload)
    .then(response => response.data)
}

export async function getKWSyncWorklistCSV (jobId: number, namespace: KWSyncGateNamespace): Promise<Blob> {
  return await http.get(`/api/v1/pidf/kw-sync/${jobId}/worklist.csv`, {
    params: { namespace },
    responseType: 'blob'
  }).then(response => response.data)
}
